import {
  Device,
  SearchDevicesResponse,
} from "@xboxstudios/hermes-apis/xboxstudios/hermes/device/v1/device_registry_pb";
import {
  QueryClient,
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { deviceKeys, operationKeys } from "./query-keys";
import { Empty } from "google-protobuf/google/protobuf/empty_pb";
import {
  ListOperationsResponse,
  Operation,
} from "@xboxstudios/hermes-apis/xboxstudios/hermes/operations/v1/operations_pb";
import {
  getDevice,
  getDeviceByUserCode,
  getDeviceList,
  sendDeviceCommand,
} from "@utils/hermes-api/device";
import { getOperation, listOperations } from "@utils/hermes-api/operation";
import { gRPCError, ListQueryOptions } from "@utils/hermes-api/types";
import { getDeviceNameFromResource } from "@utils/hermes-api/names";
import { QueryHookOptions, InfiniteQueryHookOptions } from "./utils";

export const useDeviceQuery = (
  deviceName: string,
  queryOptions: QueryHookOptions<Device, gRPCError> = {}
) => {
  return useQuery({
    queryKey: deviceKeys.single(deviceName),
    queryFn: () => getDevice(deviceName),
    ...queryOptions,
  });
};

export const useDeviceCodeQuery = (
  deviceCode: string,
  queryOptions: QueryHookOptions<Device, gRPCError> = {}
) => {
  const queryClient = useQueryClient();
  return useQuery<Device, gRPCError>({
    queryKey: deviceKeys.code(deviceCode),
    ...queryOptions,
    queryFn: async () => {
      const device = await getDeviceByUserCode(deviceCode);
      // cache the device
      if (device) {
        queryClient.setQueryData(deviceKeys.single(device.getName()), device);
      }
      return device;
    },
  });
};

export const useDeviceListInfiniteQuery = (
  filters: ListQueryOptions = {},
  queryOptions: InfiniteQueryHookOptions<SearchDevicesResponse, gRPCError> = {}
) => {
  const queryClient = useQueryClient();
  return useInfiniteQuery({
    queryKey: deviceKeys.infiniteListFiltered(filters),
    initialPageParam: "",
    queryFn: async ({ pageParam }) => {
      const res = await getDeviceList({
        ...filters,
        pageToken: pageParam as string,
      });

      // cache each device individually
      res
        .getDevicesList()
        .forEach((d) =>
          queryClient.setQueryData(deviceKeys.single(d.getName()), d)
        );

      return res;
    },
    getNextPageParam: (lastPage, pages) =>
      lastPage.getNextPageToken() || undefined,
    placeholderData: keepPreviousData,
    ...queryOptions,
  });
};

export const useDeviceListQuery = (
  filters: ListQueryOptions = {},
  queryOptions: QueryHookOptions<SearchDevicesResponse, gRPCError> = {}
) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: deviceKeys.listFiltered(filters),
    ...queryOptions,
    queryFn: async () => {
      const res = await getDeviceList(filters);
      // cache each device individually
      for (const d of res.getDevicesList()) {
        queryClient.setQueryData(deviceKeys.single(d.getName()), d);
      }
      return res;
    },
  });
};

export const useDeviceMutation = <TVariables>(
  mutateOptions: UseMutationOptions<Device, gRPCError, TVariables> = {}
) => {
  const queryClient = useQueryClient();
  return useMutation<Device, gRPCError, TVariables>({
    ...mutateOptions,
    onSuccess: (device, variables, context, meta) => {
      device.hasDeleteTime();
      // set/update individual cached device
      // queryClient.setQueryData(deviceKeys.single(device.getName()), device);
      queryClient.invalidateQueries({
        queryKey: deviceKeys.single(device.getName()),
      });

      // TODO: Determine what a good query key is for this scenario
      queryClient.invalidateQueries({
        queryKey: deviceKeys.allLists,
        exact: false,
      });

      if (mutateOptions.onSuccess) {
        mutateOptions.onSuccess(device, variables, context, meta);
      }
    },
  });
};

export const useDeviceCommandMutation = (
  mutateOptions: UseMutationOptions<
    Operation,
    gRPCError,
    Parameters<typeof sendDeviceCommand>
  > = {}
) => {
  const queryClient = useQueryClient();
  return useMutation<
    Operation,
    gRPCError,
    Parameters<typeof sendDeviceCommand>
  >({
    mutationKey: deviceKeys.commandMutation,
    mutationFn: (args) => sendDeviceCommand(...args),
    ...mutateOptions,
    onSuccess: (operation, variables, context, meta) => {
      queryClient.setQueryData(
        operationKeys.single(operation.getName()),
        operation
      );
      queryClient.invalidateQueries({
        queryKey: operationKeys.list(operation.getDeviceName()),
        exact: false,
      });
      if (mutateOptions.onSuccess) {
        mutateOptions.onSuccess(operation, variables, context, meta);
      }
    },
  });
};

export const useOperationQuery = (
  operationName: string,
  queryOptions: QueryHookOptions<Operation, gRPCError> = {}
) => {
  return useQuery({
    queryKey: operationKeys.single(operationName),
    queryFn: () => getOperation(operationName),
    ...queryOptions,
  });
};

export const useOperationListQuery = (
  resourceOwnerName: string,
  queryOptions: QueryHookOptions<ListOperationsResponse, gRPCError> = {},
  requestOptions: ListQueryOptions = {}
) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: operationKeys.listFiltered(resourceOwnerName, requestOptions),
    ...queryOptions,
    queryFn: async () => {
      const res = await listOperations(resourceOwnerName, requestOptions);
      // cache each operation individually
      for (const o of res.getOperationsList()) {
        queryClient.setQueryData(operationKeys.single(o.getName()), o);
      }
      return res;
    },
  });
};

export const useOperationListInfiniteQuery = (
  resourceOwnerName: string,
  queryOptions: InfiniteQueryHookOptions<
    ListOperationsResponse,
    gRPCError
  > = {},
  requestOptions: ListQueryOptions = {}
) => {
  const queryClient = useQueryClient();

  return useInfiniteQuery({
    queryKey: operationKeys.infiniteListFiltered(
      resourceOwnerName,
      requestOptions
    ),
    initialPageParam: "",
    queryFn: async ({ pageParam }) => {
      const res = await listOperations(resourceOwnerName, {
        ...requestOptions,
        pageToken: pageParam as string,
      });

      // cache each operation individually
      res
        .getOperationsList()
        .forEach((o) =>
          queryClient.setQueryData(operationKeys.single(o.getName()), o)
        );

      return res;
    },
    getNextPageParam: (lastPage, pages) =>
      lastPage.getNextPageToken() || undefined,
    placeholderData: keepPreviousData,
    ...queryOptions,
  });
};

export const sortOperationListByDate = (ops: Operation[]) => {
  return ops.sort(
    (a, b) =>
      // @ts-ignore
      b.getUpdateTime()?.getSeconds() - a.getUpdateTime()?.getSeconds()
  );
};

export const useOperationMutation = (
  operationName: string,
  mutationOptions: UseMutationOptions<Empty, gRPCError> = {}
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...mutationOptions,
    onSuccess: (data, variables, context, meta) => {
      queryClient.invalidateQueries({
        queryKey: operationKeys.single(operationName),
        exact: false,
      });
      if (mutationOptions.onSuccess) {
        mutationOptions.onSuccess(data, variables, context, meta);
      }
    },
  });
};

export const removeOperationFromListCache = (
  operationName: string,
  queryClient: QueryClient
) => {
  const deviceName = getDeviceNameFromResource(operationName);
  const key = operationKeys.list(deviceName);
  const opList = queryClient.getQueryData<ListOperationsResponse>(key);
  if (opList) {
    const list = opList
      .getOperationsList()
      .filter((o) => o.getName() !== operationName);
    opList.setOperationsList(list);
    queryClient.setQueryData(key, opList);
  }
};
